import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  BarChart3, 
  TrendingUp, 
  PieChart as PieChartIcon,
  DollarSign,
  Building2,
  Mail,
  Loader2,
  Send,
  Download
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
  Area,
  AreaChart
} from "recharts";
import axios from "axios";
import { toast } from "sonner";
import { ExportAllButton } from "@/components/assets/ExportAllButton";

const COLORS = ['#3b82f6', '#06b6d4', '#8b5cf6', '#f59e0b', '#10b981'];

const AnalyticsPage = () => {
  const { token, user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingAlerts, setSendingAlerts] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);

  const tooltipStyle = { backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' };
  const labelStyle = { color: '#f8fafc' };
  const barRadius = [4, 4, 0, 0];

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/portfolio`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const sendLeaseAlerts = async () => {
    setSendingAlerts(true);
    try {
      const response = await axios.post(`${API}/notifications/send-lease-alerts`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Failed to send alerts");
    } finally {
      setSendingAlerts(false);
    }
  };

  const handleDownloadPortfolio = async () => {
    setDownloadingReport(true);
    try {
      const response = await axios.get(`${API}/reports/portfolio`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `BlueElement_Portfolio_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Portfolio report downloaded");
    } catch (error) {
      toast.error("Failed to generate report");
    } finally {
      setDownloadingReport(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₦0";
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatShortCurrency = (amount) => {
    if (!amount) return "₦0";
    if (amount >= 1000000000) return `₦${(amount / 1000000000).toFixed(1)}B`;
    if (amount >= 1000000) return `₦${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `₦${(amount / 1000).toFixed(0)}K`;
    return `₦${amount}`;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="analytics-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">Portfolio Analytics</h1>
            <p className="text-slate-400 mt-1">Insights and performance metrics for your assets</p>
          </div>
          <div className="flex items-center gap-3">
            <ExportAllButton token={token} testId="export-all-btn-analytics" />
            <Button
              variant="outline"
              className="btn-secondary"
              onClick={handleDownloadPortfolio}
              disabled={downloadingReport}
              data-testid="download-portfolio-report-btn-analytics"
            >
              {downloadingReport ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              PDF Report
            </Button>
            <Button 
              onClick={sendLeaseAlerts}
              disabled={sendingAlerts}
              className="btn-primary"
              data-testid="send-alerts-btn"
            >
              {sendingAlerts ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Send Lease Alerts
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="card-dashboard">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Assets</p>
                  <p className="text-2xl font-bold text-white">{analytics?.summary?.total_assets || 0}</p>
                </div>
                <Building2 className="w-8 h-8 text-primary" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="card-dashboard">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Portfolio Value</p>
                  <p className="text-2xl font-bold text-white mono">
                    {formatShortCurrency(analytics?.summary?.total_value)}
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="card-dashboard">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Active Leases</p>
                  <p className="text-2xl font-bold text-white">{analytics?.summary?.active_leases || 0}</p>
                </div>
                <DollarSign className="w-8 h-8 text-cyan-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="card-dashboard">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Avg Monthly Collected</p>
                  <p className="text-2xl font-bold text-white mono">
                    {formatShortCurrency(analytics?.summary?.monthly_rental)}
                  </p>
                </div>
                <BarChart3 className="w-8 h-8 text-violet-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 1 */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Portfolio Value Over Time */}
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
                Portfolio Value Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={analytics?.value_history || []}>
                    <defs>
                      <linearGradient id="valueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} tickFormatter={formatShortCurrency} />
                    <Tooltip 
                      contentStyle={tooltipStyle}
                      labelStyle={labelStyle}
                      formatter={(value) => [formatCurrency(value), 'Value']}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      fill="url(#valueGradient)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Rental Income */}
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-cyan-500" />
                Monthly Rental Income
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics?.monthly_income || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} tickFormatter={formatShortCurrency} />
                    <Tooltip 
                      contentStyle={tooltipStyle}
                      labelStyle={labelStyle}
                      formatter={(value) => [formatCurrency(value), 'Income']}
                    />
                    <Bar dataKey="income" fill="#06b6d4" radius={barRadius} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Rent Collection */}
        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="card-dashboard lg:col-span-2" data-testid="rent-collection-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-500" />
                Rent Collection — Current Period
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                  <p className="text-slate-400 text-sm">Collected</p>
                  <p className="text-2xl font-bold text-emerald-400 mono" data-testid="rent-collected-value">
                    {formatShortCurrency(analytics?.rent_collection?.collected)}
                  </p>
                  <p className="text-slate-500 text-xs mt-1">{analytics?.rent_collection?.paid_count || 0} lease(s) paid</p>
                </div>
                <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <p className="text-slate-400 text-sm">Outstanding</p>
                  <p className="text-2xl font-bold text-amber-400 mono" data-testid="rent-outstanding-value">
                    {formatShortCurrency(analytics?.rent_collection?.outstanding)}
                  </p>
                  <p className="text-slate-500 text-xs mt-1">{analytics?.rent_collection?.pending_count || 0} lease(s) pending</p>
                </div>
              </div>
              {(() => {
                const total = (analytics?.rent_collection?.collected || 0) + (analytics?.rent_collection?.outstanding || 0);
                const pct = total ? Math.round(((analytics?.rent_collection?.collected || 0) / total) * 100) : 0;
                return (
                  <div className="mt-6">
                    <div className="flex justify-between text-xs text-slate-400 mb-2">
                      <span>Collection rate</span>
                      <span data-testid="collection-rate">{pct}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                      <div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${pct}%` }}></div>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-white">Paid vs Pending</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={analytics?.payment_status || []} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={5} dataKey="value">
                      <Cell fill="#10b981" />
                      <Cell fill="#f59e0b" />
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} formatter={(value, name, props) => [`${value} lease(s) · ${formatCurrency(props.payload.amount)}`, props.payload.name]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap justify-center gap-4 mt-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                  <span className="text-slate-400 text-sm">Paid</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                  <span className="text-slate-400 text-sm">Pending</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 2 */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Asset Type Distribution */}
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <PieChartIcon className="w-5 h-5 text-primary" />
                Asset Types
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={analytics?.type_distribution || []}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {(analytics?.type_distribution || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={tooltipStyle}
                      formatter={(value, name, props) => [value, props.payload.name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap justify-center gap-4 mt-2">
                {(analytics?.type_distribution || []).map((item, index) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index] }}></div>
                    <span className="text-slate-400 text-sm">{item.name} ({item.value})</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* State Distribution */}
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-white">Top Locations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(analytics?.state_distribution || []).map((item, index) => (
                  <div key={item.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                      <span className="text-slate-300">{item.name}</span>
                    </div>
                    <Badge className="bg-slate-800 text-slate-300 border-slate-700">
                      {item.value} {item.value === 1 ? 'asset' : 'assets'}
                    </Badge>
                  </div>
                ))}
                {(!analytics?.state_distribution || analytics.state_distribution.length === 0) && (
                  <p className="text-slate-500 text-center py-4">No location data yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Document Status */}
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-white">Document Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={analytics?.document_status || []}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      <Cell fill="#10b981" />
                      <Cell fill="#f59e0b" />
                      <Cell fill="#ef4444" />
                    </Pie>
                    <Tooltip 
                      contentStyle={tooltipStyle}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap justify-center gap-4 mt-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                  <span className="text-slate-400 text-sm">Complete</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                  <span className="text-slate-400 text-sm">In Progress</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-slate-400 text-sm">Missing</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Subscription Notice */}
        {user?.subscription_plan === "free" && (
          <Card className="bg-primary/10 border-primary/30">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h3 className="text-white font-semibold">Unlock Advanced Analytics</h3>
                <p className="text-slate-400 text-sm mt-1">
                  Upgrade to Professional or Enterprise for detailed performance reports and trend analysis.
                </p>
              </div>
              <Button className="btn-primary" asChild>
                <a href="/pricing">Upgrade Now</a>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AnalyticsPage;
